import { useEffect, useState } from 'react';
import StarterKit from '@tiptap/starter-kit';
import { useEditor } from '@tiptap/react';
import TextAlignExtension from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Placeholder from '@tiptap/extension-placeholder';
import Mention from '@tiptap/extension-mention';
import { SuggestionOptions } from '@tiptap/suggestion';

export interface MentionSuggestion {
  id: string;
  label: string;
  avatar?: string;
  initials?: string;
}

interface UseRichTextEditorProps {
  key: string;
  value: string;
  onUpdate?: (value: string) => void;
  onUpdateText?: (value: string) => void;
  onBlur?: (value: string) => void;
  onSelectionChange?: (selection: {
    from: number;
    to: number;
    coordinates: { left: number; top: number };
  }) => void;
  autoFocus?: boolean;
  placeholder?: string;
  mentionSuggestion?: Partial<SuggestionOptions<MentionSuggestion>>;
}

export const useRichTextEditor = ({
  key,
  value,
  onUpdate,
  onUpdateText,
  onBlur,
  onSelectionChange,
  autoFocus,
  placeholder,
  mentionSuggestion,
}: UseRichTextEditorProps) => {
  const [isTextSelected, setIsTextSelected] = useState(false);

  const extensions = [
    // StarterKit v3 bundles Link, Underline and ListKeymap by default, so they
    // are no longer registered separately. Link is configured inline here.
    StarterKit.configure({
      link: {
        autolink: true,
        HTMLAttributes: {
          style: 'text-decoration: underline; color: #367bff;',
        },
      },
    }),
    Highlight,
    TextAlignExtension,
    TaskList,
    TaskItem.configure({
      nested: true,
    }),
    Placeholder.configure({
      placeholder: placeholder || 'Write your notes here',
    }),
  ];

  // Add mention extension if suggestion is provided
  if (mentionSuggestion) {
    extensions.push(
      Mention.configure({
        HTMLAttributes: {
          class: 'mention',
          style: 'color: #367bff; background-color: rgba(54, 123, 255, 0.1); padding: 0.2rem 0.4rem; border-radius: 0.3rem;',
        },
        suggestion: mentionSuggestion as SuggestionOptions,
      }),
    );
  }

  const editor = useEditor(
    {
      editable: true,
      injectCSS: false,
      content: value,
      autofocus: autoFocus || false,
      // v3 disables per-transaction rerenders by default; the toolbar relies on
      // editor.isActive(...) staying in sync, so re-enable it.
      shouldRerenderOnTransaction: true,
      extensions,
      onUpdate: ({ editor }) => {
        onUpdate && onUpdate(editor.getHTML());
        onUpdateText && onUpdateText(editor.getText());
      },
      onBlur: ({ editor }) => {
        onBlur && onBlur(editor.getHTML());
      },
      onSelectionUpdate: ({ editor }) => {
        const { selection } = editor.state;
        const { from, to } = selection;
        onSelectionChange &&
          onSelectionChange({
            from,
            to,
            coordinates: editor.view.coordsAtPos(from),
          });

        if (from - to === 0) {
          setIsTextSelected(false);
          return;
        }

        setIsTextSelected(true);
      },
    },
    [key],
  );

  useEffect(() => {
    // A recreated/unmounted editor (e.g. navigating between contacts changes
    // `key`) is destroyed but still referenced here; its schema is null, so
    // getHTML() would throw "Cannot read properties of null (reading 'cached')".
    if (editor && !editor.isDestroyed && value !== editor.getHTML()) {
      // v3 emits an update by default; keep the v2 silent behaviour to avoid
      // feeding the change straight back through onUpdate.
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  return { editor, isTextSelected };
};
